import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, shallowEqual } from "react-redux";

/**
 * Custom hook to redirect users back to NewAnalytics when custom data is missing
 * This prevents broken state when users navigate directly to analytics routes or refresh
 */
export const useCustomDataRedirect = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if accessed through dataset parameter (from NewAnalytics)
  const datasetParam = searchParams.get('dataset');
  
  // Get custom data from Redux to check if it was loaded through NewAnalytics
  const { raw_data: customRawData } = useSelector(
    (state) => ({
      raw_data: state.audit?.raw_data,
    }),
    shallowEqual
  );

  // Track which dataset parameter we've already validated to prevent redirects when query params change
  const validatedDatasetRef = useRef(null);
  const redirectTimerRef = useRef(null);

  // Check if custom data is available when accessed via dataset parameter
  useEffect(() => {
    if (datasetParam) {
      const hasCustomData = customRawData && 
                           Array.isArray(customRawData) && 
                           customRawData.length > 0 && 
                           customRawData[0]?.datasetMetadata;
      
      // If we've already validated this specific dataset parameter, skip redirect check
      // This prevents redirects when query params change (like adding csv=1)
      const alreadyValidated = validatedDatasetRef.current === datasetParam;
      
      if (!hasCustomData && !alreadyValidated) {
        // Mark this dataset as being validated
        validatedDatasetRef.current = datasetParam;
        
        // Add a small delay to allow Redux state to stabilize
        redirectTimerRef.current = setTimeout(() => {
          // Double-check the data is still missing before redirecting
          const stillMissing = !customRawData || 
                              !Array.isArray(customRawData) || 
                              customRawData.length === 0 || 
                              !customRawData[0]?.datasetMetadata;
          
          if (stillMissing) {
            console.log("Analytics component accessed via dataset parameter but no custom data found, redirecting to NewAnalytics");
            navigate('/leader/audit/new-analytics', { replace: true });
          }
        }, 200);
      } else if (hasCustomData) {
        // If we have data, mark this dataset as validated and clear any pending redirect
        validatedDatasetRef.current = datasetParam;
        if (redirectTimerRef.current) {
          clearTimeout(redirectTimerRef.current);
          redirectTimerRef.current = null;
        }
        
        console.log("Analytics component accessed with custom data:", {
          dataset: datasetParam,
          metadata: customRawData[0]?.datasetMetadata,
          responseCount: customRawData[0]?.responses?.length
        });
      }
    } else {
      // If dataset parameter is removed, reset validation tracking
      validatedDatasetRef.current = null;
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    }

    // Cleanup timer on unmount
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, [datasetParam, customRawData, navigate]);

  return {
    datasetParam,
    hasCustomData: customRawData && 
                   Array.isArray(customRawData) && 
                   customRawData.length > 0 && 
                   customRawData[0]?.datasetMetadata
  };
}; 